'use client';

/* eslint-disable prettier/prettier */
import * as React from 'react';
import { useEffect } from 'react';
import {
  type ColumnDef,
  type ColumnFiltersState,
  type PaginationState,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { renderPageNumbers } from 'utils/renderPagination';

import { Button } from 'app/components/ui/button';

import { Input } from 'app/components/ui/input';
import { Toaster } from 'app/components/ui/toaster';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Eye,
  MoreHorizontal,
  SearchIcon,
  SquarePen,
  Trash,
} from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'app/components/ui/table';
import { useDispatch } from 'react-redux';
import useDebounce from 'utils/hooks/debounce-hook';
import { convertDateFormat } from 'utils/format-date';
import { Link, useNavigate } from 'react-router-dom';

import { useBlogsSlice } from '../../slice';
import CategoryDialog from './CategoryDialog';
import Swal from 'sweetalert2';

export function DataTable() {
  const { useGetCategoriesQuery, useDeleteCategoriesMutation } =
    useBlogsSlice();
  const [getCategories, { data: categories, isLoading }] =
    useGetCategoriesQuery();
  const [
    deleteCategory,
    { isLoading: isDeleting, data: deleteted, error: deleteError },
  ] = useDeleteCategoriesMutation();

  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: '',
      header: 'Sr. No.',
      cell: ({ row }) => (
        <div className="pl-1">
          {row.index + pagination.pageIndex * pagination.pageSize + 1}
        </div>
      ),
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div>
          <Link
            to={`/blogs/sub-categories/${row.original.id}`}
            className="text-primary hover:underline"
          >
            {row.getValue('name')}
          </Link>
        </div>
      ),
    },
    {
      accessorKey: 'subcategories',
      header: 'Subcategories',
      cell: ({ row }) => {
        const subcategories: any = row.getValue('subcategories');
        return (
          <div className="flex items-center">
            {subcategories?.length > 0 ? (
              <div className="flex items-center space-x-2">
                {subcategories.map((subcategory: any, index: number) => (
                  <div
                    key={subcategory.id}
                    className="flex items-center space-x-1"
                  >
                    <span>{subcategory.name}</span>
                    {index < subcategories.length - 1 &&
                      subcategories.length > 1 && <span>,</span>}
                  </div>
                ))}
              </div>
            ) : (
              <span>No Subcategories</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'created_at',
      header: 'Created Date',
      cell: ({ row }) => (
        <div className="whitespace-nowrap">
          {convertDateFormat(row.getValue('created_at'))}
        </div>
      ),
    },

    ,
    {
      accessorKey: '',
      header: 'Action',
      cell: ({ row }) => (
        <div className="flex items-center  db-table-action space-x-2 pr-2">
          <div>
            <Trash
              className="delete icon"
              onClick={() => {
                handleDelete(row.original.id);
              }}
            />
          </div>
          <div>
            <SquarePen
              className="edit-line icon "
              onClick={() => {
                setOpen(true);
                setSelected(row.original);
              }}
            />
          </div>
        </div>
      ),
    },
  ];

  const dispatch = useDispatch();

  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  const [search, setSearch] = React.useState('');
  const searchDebounce = useDebounce(search, 300);

  const [selected, setSelected] = React.useState<number>();

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const table = useReactTable({
    data: categories?.response?.data || [],
    columns,
    pageCount: categories?.response?.totalPages || 0,
    rowCount: categories?.response?.totalCount || 0,
    manualPagination: true,
    onPaginationChange: setPagination,
    autoResetPageIndex: false,
    // Other table configurations
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  useEffect(() => {
    getCategories({
      page: pagination.pageIndex + 1,
      search: searchDebounce,
      limit: pagination.pageSize,
    });
  }, [pagination, searchDebounce]);

  const handleDelete = (id: number) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then(result => {
      if (result.isConfirmed) {
        deleteCategory(id);
      }
    });
  };

  useEffect(() => {
    if (deleteted) {
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Category deleted successfully!',
        showConfirmButton: false,
        timer: 1500,
      });
    }
  }, [deleteted]);

  useEffect(() => {
    if (deleteError) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to delete category. Please try again.',
      });
    }
  }, [deleteError]);

  return (
    <div className="w-full  rounded-[10px] p-5 border border-solid dark:border-white">
      <div className="flex justify-between items-center mb-[15px]">
        <div className="relative w-full">
          <div className="absolute left-[10px] top-[10px]">
            <SearchIcon className="h-4 w-4" />
          </div>
          <Input
            placeholder="Search by name"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="max-w-sm pl-[35px] "
          />
        </div>
        <div>
          <Button
            onClick={() => {
              setOpen(true);
            }}
          >
            Add Category
          </Button>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader className="">
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map(row => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map(cell => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  {isLoading ? 'Loading...' : 'No results found'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}

      <div className="flex flex-col md:flex-row items-center justify-between py-4 space-y-4 md:space-y-0">
        <div className="text-sm text-muted-foreground">
          {table.getFilteredSelectedRowModel().rows.length} of{' '}
          {table.getFilteredRowModel().rows.length} row(s) selected.
        </div>

        <div className="flex items-center space-x-1">
          {/* First Page */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.setPageIndex(0)}
            disabled={pagination.pageIndex === 0}
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>

          {/* Previous Page */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={pagination.pageIndex === 0}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {/* Page Numbers with Ellipsis */}
          {renderPageNumbers(pagination.pageIndex, table.getPageCount()).map(
            (page, idx) =>
              typeof page === 'number' ? (
                <Button
                  key={idx}
                  variant={
                    pagination.pageIndex === page ? 'default' : 'outline'
                  }
                  size="sm"
                  onClick={() => table.setPageIndex(page)}
                >
                  {page + 1}
                </Button>
              ) : (
                <Button key={idx} variant="ghost" size="sm" disabled>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              ),
          )}

          {/* Next Page */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          {/* Last Page */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Toaster />

      <CategoryDialog
        isOpen={open}
        onClose={setOpen}
        categoryToEdit={selected}
      />
    </div>
  );
}
